import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Table } from 'primeng/table';
import { JwtService } from '../../auth/jwtservice';
import { CallingService } from '../../services/common/calling.service';
import { ToasterService } from '../../services/common/toaster.service';
import { TokenPropertyUrl } from '../../../appconfig';
import { SalesService } from '../../main-layout/sales/sales.service';

/**
 * CommonTableService
 * Shared business logic for all table variants (light and dark themes)
 * Handles filtering, sorting, selection, dropdown actions, and user interactions
 */
@Injectable({
    providedIn: 'root'
})
export class CommonTableService {

    constructor(
        private route: Router,
        private jwtService: JwtService,
        private callingService: CallingService,
        private toasterService: ToasterService,
        private salesService: SalesService
    ) { }

    /**
     * Filter table data globally
     * @param dt PrimeNG Table reference
     * @param searchValue Search term
     * @param filterType Filter type (contains, equals, etc.)
     */
    filterData(dt: Table | undefined, searchValue: string, filterType: string = 'contains'): void {
        if (dt) {
            dt.filterGlobal(searchValue, filterType);
        }
    }

    /**
     * Handle dropdown change event
     * @param event Event object with new value
     * @param selectedRow Row being modified
     * @param options Available dropdown options
     * @returns Filtered options
     */
    onDropdownClick(event: any, selectedRow: any, options: any[]): any {
        return {
            route: event.value,
            currentRow: selectedRow
        };
    }

    /**
     * Toggle select all checkbox
     * @param dataCopy Array of data rows
     * @param checked Whether all should be selected
     * @returns Updated data array with selection state
     */
    toggleSelectAll(dataCopy: any[], checked: boolean): any[] {
        dataCopy.forEach(item => item['selection'] = checked);
        return dataCopy;
    }

    /**
     * Check if all items are selected
     * @param dataCopy Array of data rows
     * @returns Boolean indicating if all are selected
     */
    updateSelectAll(dataCopy: any[]): boolean {
        return dataCopy.every(item => item['selection']);
    }

    /**
     * Handle individual checkbox change
     * @param dataCopy Array of data rows
     * @param currentRow Row being modified
     * @param checked Whether row is checked
     * @returns Array of selected rows
     */
    onCheckboxChange(dataCopy: any[], currentRow: any, checked: boolean): any[] {
        currentRow.checked = checked;
        return dataCopy.filter(item => item.checked === true);
    }

    /**
     * Handle radio button selection (single selection)
     * @param currentRow Row being selected
     * @returns Selected row
     */
    onRadioChange(currentRow: any): any {
        currentRow.selection = true;
        return currentRow;
    }

    /**
     * Truncate text to specified length
     * @param data Text to truncate
     * @param textLength Maximum length
     * @returns Truncated text with ellipsis
     */
    setTextLength(data: string, textLength?: number): string {
        if (!data) {
            return '';
        }
        if (textLength && data.length > textLength) {
            return data.slice(0, textLength) + '...';
        }
        return data;
    }

    /**
     * Get row style if enabled
     * @param rowId Row ID
     * @param rowStyles Array of row styles
     * @param enableRowStyles Whether row styling is enabled
     * @returns Row style object or empty object
     */
    getRowStyle(rowId: number | undefined, rowStyles: any[], enableRowStyles: boolean): any {
        if (!enableRowStyles || !rowId) return {};
        const rowStyle = rowStyles.find(style => style.id === rowId);
        return rowStyle ? rowStyle.style : {};
    }

    /**
     * Filter dropdown options based on conditions
     * Used for payment screen to remove conflicting options
     * @param options Original options list
     * @param selectedValue Currently selected value
     * @param currentRecord Current row data
     * @returns Filtered options array
     */
    filterOptions(options: any[], selectedValue: any, currentRecord: any): any[] {
        if (!options) return [];

        return options.filter((option: any) => {
            // Remove allotment if booking is selected
            if (selectedValue === 'Booking' && option.value === 'allotment') {
                return false;
            }
            // Remove booking if allotment is selected
            if (selectedValue === 'Allotment' && option.value === 'booking') {
                return false;
            }
            // Remove cancel if booking is selected and cancellation is requested
            if (selectedValue === 'Booking' && option.value === 'cancel' && currentRecord?.isRequestedForCancellation === true) {
                return false;
            }
            return true;
        });
    }

    /**
     * Handle contact column eye icon click
     * @param cellData Contact data
     * @returns Object with contact data
     */
    onClickEyeOfContactColumn(cellData: any): any {
        return {
            viewContactModal: true,
            viewedContactNumber: cellData
        };
    }

    /**
     * Initiate call to contact
     * @param viewedContactNumber Contact number to call
     */
    async clickToCall(viewedContactNumber: string): Promise<void> {
        const currentUserContactNumber = this.jwtService.getUserPropertyValueFromToken(TokenPropertyUrl.contactNumber)
            .replace(/(\d{5})(\d{5})/, '$1-$2');

        const callContactData = {
            to: viewedContactNumber.replace(/-/g, '').toString(),
            from: currentUserContactNumber.replace(/-/g, '').toString()
        };

        try {
            await this.callingService.postCallContacts(callContactData).toPromise();
            this.toasterService.showSuccess('You will receive call shortly.');
        } catch (error) {
            this.toasterService.showError('System is not able to connect, Kindly connect with administrator.');
        }
    }

    /**
     * Handle hyperlink click in table cell
     * @param event Mouse event
     * @param currentRow Row data
     * @param onClick Optional click handler function
     */
    handleHyperlinkClick(event: MouseEvent, currentRow: any, onClick?: (row: any) => void): void {
        event.stopPropagation();
        if (onClick) {
            onClick(currentRow);
        }
    }
}
